import React, { useState } from "react";
import axios from "axios";
import { API_ENDPOINTS } from "../config/api";
import { Mail, MessageSquare, Shield, Github, Linkedin } from "lucide-react";
import "../styles/contact.css";

const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
    honeypot: "",
  });
  const [status, setStatus] = useState({ state: "idle", msg: "" });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.honeypot) {
      return;
    }

    setStatus({ state: "loading", msg: "Sending..." });

    try {
      const response = await axios.post(API_ENDPOINTS.contact, {
        name: formData.name.trim(),
        email: formData.email.trim(),
        subject: formData.subject.trim(),
        message: formData.message.trim(),
      });

      if (response.status === 201) {
        setStatus({ state: "success", msg: "Thanks! I'll reply soon." });
        setFormData({
          name: "",
          email: "",
          subject: "",
          message: "",
          honeypot: "",
        });
      } else {
        setStatus({ state: "error", msg: "Could not send. Try again shortly." });
      }
    } catch (error) {
      setStatus({
        state: "error",
        msg:
          error.response?.data?.message ||
          "Could not send. Try again shortly.",
      });
    }
  };

  return (
    <div className="contact-page">
      <section className="container py-5">
        <div className="contact-hero">
          <div>
            <p className="eyebrow">Contact</p>
            <h1 className="display-6 fw-bold">Let's build something accountable</h1>
            <p className="lead">
              Reach out for collaborations, audits, or advisory on AI, mobility,
              and data governance. Your message goes straight to me, no bots, no
              spam.
            </p>
            <div className="contact-tags">
              <span className="tag">Operational analytics</span>
              <span className="tag">AI for efficiency</span>
              <span className="tag">Data governance</span>
            </div>
          </div>
          <div className="contact-cards">
            <div className="info-card">
              <div className="icon-pill">
                <Mail size={18} />
              </div>
              <h6>Email</h6>
              <p className="mb-2 text-muted">mohamed@mohamedgad.com</p>
              <a
                className="btn btn-outline-primary btn-sm"
                href="mailto:mohamed@mohamedgad.com"
              >
                Send email
              </a>
            </div>
            <div className="info-card">
              <div className="icon-pill">
                <Linkedin size={18} />
              </div>
              <h6>LinkedIn</h6>
              <p className="mb-2 text-muted">mohamed-gad-msds-6b286b1b6</p>
              <a
                className="btn btn-dark btn-sm"
                href="https://www.linkedin.com/in/mohamed-gad-msds-6b286b1b6/"
                target="_blank"
                rel="noreferrer"
              >
                Connect
              </a>
            </div>
            <div className="info-card">
              <div className="icon-pill">
                <Github size={18} />
              </div>
              <h6>GitHub</h6>
              <p className="mb-2 text-muted">github.com/OldAlexhub</p>
              <a
                className="btn btn-outline-dark btn-sm"
                href="https://github.com/OldAlexhub"
                target="_blank"
                rel="noreferrer"
              >
                View repos
              </a>
            </div>
          </div>
        </div>
      </section>

      <section className="container pb-5">
        <div className="row g-4">
          <div className="col-lg-7">
            <div className="form-card">
              <div className="d-flex align-items-center mb-3">
                <MessageSquare size={18} className="me-2 text-primary" />
                <div>
                  <p className="eyebrow mb-0">Message</p>
                  <h5 className="mb-0">Send a note</h5>
                </div>
              </div>
              <form onSubmit={handleSubmit} className="row g-3">
                <div className="col-md-6">
                  <label className="form-label">Name</label>
                  <input
                    name="name"
                    type="text"
                    className="form-control"
                    placeholder="Your name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Email</label>
                  <input
                    name="email"
                    type="email"
                    className="form-control"
                    placeholder="you@example.com"
                    required
                    value={formData.email}
                    onChange={handleChange}
                  />
                </div>
                <div className="col-12">
                  <label className="form-label">Subject</label>
                  <input
                    name="subject"
                    type="text"
                    className="form-control"
                    placeholder="Project, advisory, or question"
                    required
                    value={formData.subject}
                    onChange={handleChange}
                  />
                </div>
                <div className="col-12">
                  <label className="form-label">Message</label>
                  <textarea
                    name="message"
                    className="form-control"
                    placeholder="Share context, timelines, or goals"
                    rows="5"
                    minLength="10"
                    required
                    value={formData.message}
                    onChange={handleChange}
                  />
                </div>
                <div className="d-none">
                  <label>Do not fill</label>
                  <input
                    name="honeypot"
                    value={formData.honeypot}
                    onChange={handleChange}
                  />
                </div>
                <div className="col-12">
                  <button
                    type="submit"
                    className="btn btn-primary w-100"
                    disabled={status.state === "loading"}
                  >
                    {status.state === "loading" ? "Sending..." : "Send securely"}
                  </button>
                  {status.msg && (
                    <div
                      className={`mt-2 small ${
                        status.state === "error"
                          ? "text-danger"
                          : "text-success"
                      }`}
                    >
                      {status.msg}
                    </div>
                  )}
                </div>
              </form>
            </div>
          </div>
          <div className="col-lg-5">
            <div className="info-card h-100">
              <div className="d-flex align-items-center mb-2">
                <Shield size={18} className="me-2 text-primary" />
                <div>
                  <p className="eyebrow mb-0">Safety</p>
                  <h5 className="mb-0">Spam and scam protection</h5>
                </div>
              </div>
              <ul className="list-unstyled mb-0">
                <li className="mb-2">
                  - Messages go directly to my inbox. No third-party storage.
                </li>
                <li className="mb-2">
                  - Hidden honeypot field blocks most bots.
                </li>
                <li className="mb-2">
                  - I never ask for sensitive info over email; verification
                  happens live.
                </li>
                <li className="mb-2">
                  - Replies come only from mohamed@mohamedgad.com.
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
